import { Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, effect, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { BackendService } from '@core/services/backend.service';
import { BaseComponent } from '@shared/classes/base.class';
import { CampaignTypes, DeviceType, MediaQuerySize } from '@shared/constants';
import { CampaignDTO } from '@shared/dto';
import { Campaign } from '@shared/factory';
import { AnyChallenge, CampaignsByPhase } from '@shared/models';
import { IS_MOBILE, sendIdBeacon } from '@shared/utils';

@Component({
  selector: 'b2c-root',
  imports: [RouterOutlet],
  template: '<router-outlet />',
})
export class AppComponent extends BaseComponent implements OnInit {
  private readonly platformId: object = inject(PLATFORM_ID);
  private readonly isMobile = inject(IS_MOBILE, { optional: true });
  private readonly backendService: BackendService = inject(BackendService);
  private humanBeaconSent = false;

  constructor() {
    super();
    effect(() => {
      if (this.dataService.environment().hasCampaignBis) {
        this.getBisCampaigns();
      } else {
        this.dataService.futureBisCampaignLoaded.set(true);
      }
    });
  }

  ngOnInit(): void {
    this.getCampaigns();
    if (isPlatformBrowser(this.platformId)) {
      this.sendHumanBeacon();
    }
    this.configLoaderService.loadConfig();
    if (isPlatformServer(this.platformId) && this.isMobile) {
      // Set device type on server for SSR
      this.mediaObserverService.device.set(DeviceType.MOBILE);
      this.mediaObserverService.mqAlias.set(MediaQuerySize.XS);
    } else {
      this.observeBreakpoints();
    }
  }

  /**
   * Observe CDK Breakpoints
   */
  private observeBreakpoints(): void {
    this.mediaObserverService
      .observe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: BreakpointState) => {
        if (state.breakpoints[Breakpoints.XSmall]) {
          this.mediaObserverService.device.set(DeviceType.MOBILE);
          this.mediaObserverService.mqAlias.set(MediaQuerySize.XS);
        } else if (state.breakpoints[Breakpoints.Small]) {
          this.mediaObserverService.device.set(DeviceType.TABLET);
          this.mediaObserverService.mqAlias.set(MediaQuerySize.SM);
        } else {
          this.mediaObserverService.device.set(DeviceType.DESKTOP);
          this.mediaObserverService.mqAlias.set(
            state.breakpoints[Breakpoints.Medium]
              ? MediaQuerySize.MD
              : MediaQuerySize.LG
          );
        }
      });
  }

  /**
   * Get Campaigns
   */
  private getCampaigns(): void {
    const campaignType =
      this.environment.projectType === this.ProjectTypes.LOYALTY_CHALLENGE
        ? CampaignTypes.LOYALTYCHALLENGE
        : CampaignTypes.PERSONALIZED_PROMOTIONS;
    this.backendService
      .loadCampaign(campaignType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (campaignsByPhase: CampaignsByPhase) => {
          const currentCampaigns = campaignsByPhase?.current ?? [];
          if (currentCampaigns.length > 0) {
            this.dataService.hasCurrentCampaign.set(true);
            this.dataService.currentCampaign.set(
              new Campaign(currentCampaigns[0])
            );
          } else {
            this.dataService.hasCurrentCampaign.set(false);
          }

          this.dataService.futureCampaignLoaded.set(true);
          if (campaignsByPhase?.future && campaignsByPhase.future.length) {
            this.dataService.hasFutureCampaign.set(true);
            this.dataService.futureCampaign.set(
              new Campaign(campaignsByPhase.future[0])
            );
          } else {
            this.dataService.hasFutureCampaign.set(false);
          }

          if (
            campaignsByPhase?.completed &&
            campaignsByPhase.completed.length
          ) {
            const campaignsMap = campaignsByPhase.completed.map(
              (c: CampaignDTO) => new Campaign(c)
            );
            const sortedCampaigns = this.sortCampaigns(campaignsMap);
            this.dataService.hasCompletedCampaigns.set(
              sortedCampaigns.length > 0
            );
            this.dataService.completedCampaigns.set(sortedCampaigns);
            sortedCampaigns.forEach((c: Campaign) => {
              this.dataService.completedLoyaltyData.update(
                (value: {
                  [campaignId: number]: {
                    loaded: boolean;
                    challenges: AnyChallenge[];
                  };
                }) => {
                  value[c.campaignId] = {
                    loaded: false,
                    challenges: [],
                  };
                  return value;
                }
              );
            });
          } else {
            this.dataService.hasCompletedCampaigns.set(false);
            this.dataService.completedCampaigns.set([]);
          }
        },
      });
  }

  /**
   * Non-blocking beacon to log real browser execution without impacting SSR.
   * Bots and link scanners that never execute JS will not trigger this call, while
   * human browsers will fire it once per page load via fetch.
   */
  private sendHumanBeacon(): void {
    if (this.humanBeaconSent || !isPlatformBrowser(this.platformId)) return;

    this.humanBeaconSent = true;

    try {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const sha256Re = /\b[a-f0-9]{64}\b/i;
      const uuidRe =
        /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;

      let untienotsId = '';
      for (const seg of pathSegments) {
        const uuidMatch = seg.match(uuidRe);
        if (uuidMatch) {
          untienotsId = uuidMatch[0];
          break;
        }
        const shaMatch = seg.match(sha256Re);
        if (shaMatch) {
          untienotsId = shaMatch[0];
          break;
        }
      }

      if (!untienotsId) return;

      sendIdBeacon('is-human', 'HUMAN DETECTED', untienotsId);
    } catch (error) {
      console.log('Human beacon error:', error);
    }
  }

  /**
   * Get BIS Campaign
   */
  private getBisCampaigns(): void {
    this.backendService
      .loadCampaign(this.CampaignTypes.LOYALTYCHALLENGE_BIS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (campaignsByPhase: CampaignsByPhase) => {
          const hasCurrentCampaigns =
            campaignsByPhase &&
            campaignsByPhase.current &&
            campaignsByPhase.current.length > 0;
          const currentCampaigns: Campaign[] = hasCurrentCampaigns
            ? campaignsByPhase.current.map(
                (campaign: CampaignDTO) => new Campaign(campaign)
              )
            : [];
          this.dataService.currentBisCampaigns.set(currentCampaigns);
          this.dataService.hasCurrentBisCampaign.set(
            currentCampaigns.length > 0
          );

          this.dataService.futureBisCampaignLoaded.set(true);
          if (
            campaignsByPhase &&
            campaignsByPhase.future &&
            campaignsByPhase.future.length
          ) {
            this.dataService.hasFutureBisCampaign.set(true);
            this.dataService.futureBisCampaign.set(
              new Campaign(campaignsByPhase.future[0])
            );
          }

          if (
            campaignsByPhase &&
            campaignsByPhase.completed &&
            campaignsByPhase.completed.length
          ) {
            const sortedCampaigns = this.sortCampaigns(
              campaignsByPhase.completed.map(
                (c: CampaignDTO) => new Campaign(c)
              )
            );
            this.dataService.hasCompletedBisCampaigns.set(
              sortedCampaigns.length > 0
            );
            this.dataService.completedBisCampaigns.set(sortedCampaigns);
            sortedCampaigns.forEach((c: Campaign) => {
              this.dataService.completedBisData.update(
                (value: {
                  [campaignId: number]: {
                    loaded: boolean;
                    challenges: AnyChallenge[];
                  };
                }) => {
                  value[c.campaignId] = {
                    loaded: false,
                    challenges: [],
                  };
                  return value;
                }
              );
            });
          }
        },
      });
  }

  /**
   * sort campaigns by end date
   * @param campaigns Array of campaigns
   * @returns {Array<Campaign>} Array of campaigns sorted by end date
   */
  private sortCampaigns(campaigns: Array<Campaign>): Array<Campaign> {
    return campaigns.sort((a: Campaign, b: Campaign) => {
      if (a.endDate < b.endDate) {
        return 1;
      } else {
        return -1;
      }
    });
  }
}
